import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { TrialAccessViewModel } from "@/hooks/useTrialAccess";

export function TrialConfirmationDialog({
  trial,
  open,
  onComplete,
  onDismiss = onComplete,
  onViewPricing,
}: {
  trial: TrialAccessViewModel;
  open: boolean;
  onComplete: () => void;
  onDismiss?: () => void;
  onViewPricing: () => void;
}) {
  const startsWithLeader = trial.role === "coach" && trial.presentation.state === "not_started";
  const title = startsWithLeader
    ? "Your full-access trial starts with your first leader"
    : "Your 7-day full-access trial is active";
  const primaryLabel = startsWithLeader
    ? "Add my first leader"
    : trial.role === "coach"
      ? "Start with my leader"
      : "Set my first focus";
  const exactEndDate = trial.presentation.trialEndsAt
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "long",
        timeZone: trial.timeZone,
      }).format(new Date(trial.presentation.trialEndsAt))
    : null;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onDismiss()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="space-y-3 pt-2 text-left">
            {startsWithLeader ? (
              <>
                <span className="block">
                  Your seven days begin when your first leader joins, so the trial does not run
                  down before you can use it together.
                </span>
                <span className="block">
                  One leader is included with Leadership and Coaching Intelligence, meeting
                  capture, proactive memory, and the Leadership Bot.
                </span>
              </>
            ) : (
              <>
                <span className="block">
                  {trial.role === "coach"
                    ? "Use Coach Veera with one leader, including Leadership and Coaching Intelligence, meeting capture, proactive memory, and the Leadership Bot."
                    : "You have full access to Leadership Intelligence, meeting capture, proactive memory, and the Leadership Bot."}
                </span>
                {exactEndDate ? (
                  <span className="block font-medium text-foreground">
                    Full access through {exactEndDate}.
                  </span>
                ) : null}
              </>
            )}
            <span className="block">
              After seven days, your data stays available. Choose a plan to keep the full experience.
            </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="ghost" onClick={onViewPricing}>
            View pricing
          </Button>
          <Button type="button" onClick={onComplete}>
            {primaryLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
